import Store from 'store/store'
import { writable } from 'utils/state'
import { Component } from 'utils/jsx'
import classnames from 'classnames'
import groupBy from 'utils/array-group-by'

import Button from 'components/dom/Button'
import Checkbox from 'components/dom/Checkbox'
import Modal from 'components/dom/Modal'
import Select from 'components/dom/Select'
import TextEditable from 'components/dom/TextEditable'

/**
 * To simplify these Components architecture and their relation, we implements a
 * React style lifecycle where the component is entirely destroyed at each
 * update, to be completely recreated with the updated props.
 *
 * The single source of truth is here the Store.formats Signal array. When a
 * selection occurs, the correspondig format object in the Store.formats array
 * is mutated, then the Signal is forced dispatch, trigerring the destruction
 * and re-rendering of all this Component.
 */

const CUSTOM_LABEL = 'Formats personnalisés'

export default class Formats extends Component {
  beforeRender () {
    this.handleScroll = this.handleScroll.bind(this)
    this.update = this.update.bind(this)
  }

  template () {
    return (
      <section class='formats' event-wheel={this.handleScroll}>
        <div class='formats__container' ref={this.ref('container')} />
      </section>
    )
  }

  afterRender () {
    Store.formats.subscribe(this.update)
    this.update()
  }

  update () {
    const groups = groupBy(Store.formats.get(), 'group')
    if (!groups[CUSTOM_LABEL]) groups[CUSTOM_LABEL] = []

    for (const name in groups) {
      if (this.refs[name]) this.refs[name].destroy()

      const formats = groups[name]
      const selected = formats.filter(format => format.selected)

      this.render((
        <Group
          name={name}
          extendable={name === CUSTOM_LABEL}
          disabled={formats.length === 0}
          selected={formats.length > 0 && selected.length === formats.length}
          indeterminate={selected.length && selected.length < formats.length}
          formats={formats}
          ref={this.ref(name)}
        />
      ), this.refs.container)
    }

    this.renderPadHack()
  }

  renderPadHack () {
    this.refs.padHack && this.refs.padHack.remove()
    this.render(<div class='overflow-padding-hack' ref={this.ref('padHack')} />, this.refs.container)
  }

  handleScroll (e) {
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return
    this.base.scrollLeft += e.deltaY
    e.preventDefault()
  }
}

class Group extends Component {
  beforeRender () {
    this.handleGroupClick = this.handleGroupClick.bind(this)
  }

  template (props) {
    const classes = classnames('formats__group', {
      'is-checked': props.selected,
      'is-disabled': props.disabled,
      'is-indeterminate': props.indeterminate
    })

    return (
      <div class={classes} data-name={props.name}>
        <div class='formats__group-title'>
          <Checkbox
            event-input={this.handleGroupClick}
            checked={props.selected}
            indeterminate={props.indeterminate}
          >{props.name}</Checkbox>
        </div>
        <ul class='formats__formats'>
          {props.formats.map(format => <Format format={format} />)}
          {
            props.extendable && <AddFormat />
          }
        </ul>
      </div>
    )
  }

  handleGroupClick (e) {
    // Update all formats of this group
    this.props.formats.forEach(format => { format.selected = e.target.checked })
    Store.formats.dispatch(Store.formats.current)
  }
}

class Format extends Component {
  beforeRender () {
    this.handleInput = this.handleInput.bind(this)
  }

  template (props) {
    const { name, dimensions, selected } = props.format
    return (
      <li
        class='formats__format'
        title={`${name} [${dimensions[0]}×${dimensions[1]}${dimensions[2]}]`}
        data-name={name}
      >
        <Checkbox checked={selected} event-input={this.handleInput}>
          {name}
        </Checkbox>
      </li>
    )
  }

  handleInput (e) {
    // Update the pointer to the relevant format
    this.props.format.selected = e.target.checked
    Store.formats.dispatch(Store.formats.current)
  }
}

class AddFormat extends Component {
  beforeRender () {
    this.openModal = this.openModal.bind(this)
    this.handleValidation = this.handleValidation.bind(this)
    this.captureEnterKey = this.captureEnterKey.bind(this)

    this.state = {
      name: writable(''),
      width: writable(''),
      height: writable(''),
      error: writable(''),
      units: [
        { value: 'px', selected: true },
        { value: 'mm' }
      ],
    }
  }

  template (props) {
    return (
      <li class='formats__format formats__format--add'>
        <Button icon='plus' event-click={this.openModal}>Ajouter un format</Button>
      </li>
    )
  }

  openModal () {
    this.render((
      <Modal class='format-modal' title='Nouveau format' ref={this.ref('modal')}>
        <div class='format-modal__form'>
          <input type='text' ref={this.ref('inputName')} event-input={e => this.state.name.set(e.target.value)} placeholder='Nom*' />
          <div class='format-modal__fieldset'>
            <input type='number' ref={this.ref('inputWidth')} event-input={e => this.state.width.set(e.target.value)} placeholder='Largeur*' />
            <input type='number' ref={this.ref('inputHeight')} event-input={e => this.state.height.set(e.target.value)} placeholder='Hauteur*' />
            <Select options={this.state.units} />
          </div>
        </div>
        <footer class='format-modal__footer'>
          <div class='format-modal__error' store-text={this.state.error} />
          <Button icon='check' event-click={this.handleValidation}>Valider</Button>
        </footer>
      </Modal>
    ), document.body)

    window.addEventListener('keyup', this.captureEnterKey)
  }

  captureEnterKey (e) {
    if (e.key !== 'Enter') return
    if (this.refs.modal && this.refs.modal.mounted) this.handleValidation()
  }

  handleValidation () {
    const errors = []

    // Reset errors
    for (const previousErrors of this.refs.modal.base.querySelectorAll('.is-invalid')) {
      previousErrors.classList.remove('is-invalid')
    }

    // Validate name (cannot be empty, cannot be already used)
    const name = this.state.name.get()
    if (!name) errors.push({ ref: this.refs.inputName, message: 'Nom requis' })
    if (Store.formats.current.find(f => f.name === name)) {
      errors.push({ ref: this.refs.inputName, message: 'Nom déjà pris' })
    }

    // Validate width (cannot be null, cannot be NaN)
    const width = this.state.width.get()
    if (!width) errors.push({ ref: this.refs.inputWidth, message: 'Largeur requise' })
    if (isNaN(width)) errors.push({ ref: this.refs.inputWidth, message: 'Largeur invalide' })

    // Validate height (cannot be null, cannot be NaN)
    const height = this.state.height.get()
    if (!height) errors.push({ ref: this.refs.inputHeight, message: 'Hauteur requise' })
    if (isNaN(height)) errors.push({ ref: this.refs.inputHeight, message: 'Hauteur invalide' })

    // Display errors, fail early if any
    if (errors.length) {
      for (const { ref } of errors) ref.classList.add('is-invalid')
      this.state.error.set(errors[0].message)
      return
    }

    // Create format
    const unit = this.state.units.find(u => u.selected).value
    const format = {
      selected: true,
      group: CUSTOM_LABEL,
      name,
      dimensions: [+width, +height, unit],
      extension: unit === 'px' ? 'png' : 'pdf'
    }

    this.refs.modal.destroy()
    window.removeEventListener('keyup', this.captureEnterKey)

    Store.formats.update(formats => [...formats, format], true)
  }
}



// WEBPACK FOOTER //
// ./src/components/dom/Formats.js